import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, Car, Star, MapPin, CheckCircle, ArrowRight } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fieldCls = 'mt-1.5 bg-gray-50 border-gray-200 focus:border-[#1a5c3b] rounded-xl h-11';

const Field = ({ id, label, children }) => (
  <div><Label htmlFor={id} className="text-gray-700 font-medium">{label}</Label>{children}</div>
);

export const VehicleContactPanel = ({ v, en, onApply }) => {
  const label = `${v.year} ${v.make} ${v.model}${v.stock_number ? ` (Stock ${v.stock_number})` : ''}`;
  const [tab, setTab] = useState('inquiry');
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: en ? 'I am interested in this vehicle.' : 'Je suis intéressé par ce véhicule.', date: '', time: '' });
  const [status, setStatus] = useState('idle');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await axios.post(`${API}/vehicle-inquiry`, { ...form, kind: tab, vehicle_id: v.id, vehicle_label: label });
      setStatus('done');
    } catch {
      setStatus('error');
    }
  };

  const tabBtn = (key, Icon, text) => (
    <button type="button" onClick={() => { setTab(key); setStatus('idle'); }} className={`flex-1 py-3 px-4 text-sm font-semibold rounded-lg transition-all flex items-center justify-center gap-2 ${tab === key ? 'bg-white text-[#1a5c3b] shadow-md' : 'text-gray-500 hover:text-gray-700'}`} data-testid={`vehicle-tab-${key}`}>
      <Icon className="h-4 w-4" />{text}
    </button>
  );

  return (
    <div className="space-y-6 lg:sticky lg:top-[164px]">
      <div className="bg-gradient-to-br from-[#1a5c3b] via-[#165432] to-[#0f3d26] rounded-2xl shadow-xl p-5 text-white" data-testid="vehicle-financing-cta">
        <p className="text-xs uppercase tracking-widest font-bold text-green-200 mb-1">{en ? 'Financing 2nd & 3rd chance' : 'Financement 2e et 3e chance'}</p>
        <h3 className="text-2xl font-black mb-2 text-white">{en ? 'Get financed for this vehicle' : 'Faites-vous financer pour ce véhicule'}</h3>
        <p className="text-sm text-white/80 mb-4">{en ? 'Free evaluation, no obligation.' : 'Évaluation gratuite, sans obligation.'}</p>
        <button type="button" onClick={onApply} className="w-full bg-white text-[#1a5c3b] font-black py-3.5 rounded-xl hover:bg-green-50 transition-colors flex items-center justify-center gap-2" data-testid="vehicle-apply-btn">
          {en ? 'Finance Me!' : 'Financez-moi!'}<ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <div className="bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden">
        <a href="tel:514-418-3968" className="block bg-gradient-to-r from-[#1a5c3b] to-[#0f3d26] p-5 text-white" data-testid="vehicle-call-link">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><Phone className="h-5 w-5" /></div>
            <div><p className="text-green-100 text-xs uppercase tracking-wider font-medium">{en ? 'Contact us' : 'Contactez-nous'}</p><p className="font-bold text-lg">514-418-3968</p></div>
          </div>
        </a>
        <div className="flex bg-gray-50 p-1 mx-4 mt-4 rounded-xl">
          {tabBtn('inquiry', Mail, en ? 'Inquiry' : 'Demande')}
          {tabBtn('testdrive', Car, en ? 'Test drive' : 'Essai routier')}
        </div>
        <div className="p-5">
          {status === 'done' ? (
            <div className="text-center py-8" data-testid="vehicle-inquiry-success">
              <CheckCircle className="w-12 h-12 text-[#1a5c3b] mx-auto mb-3" />
              <p className="font-bold text-lg text-gray-900">{en ? 'Request sent!' : 'Demande envoyée!'}</p>
              <p className="text-sm text-gray-500">{en ? 'An advisor will contact you shortly.' : 'Un conseiller vous contactera sous peu.'}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid={`${tab}-form`}>
              <div>
                <h2 className="font-bold text-xl text-gray-900">{tab === 'inquiry' ? (en ? 'Interested in this vehicle?' : 'Intéressé par ce véhicule?') : (en ? 'Book a test drive' : 'Réservez un essai routier')}</h2>
                <p className="text-sm text-gray-500 mt-1">{tab === 'inquiry' ? (en ? 'Fill out the form and we will contact you quickly.' : 'Remplissez le formulaire et nous vous contacterons rapidement.') : (en ? 'Pick a date and time that suits you.' : 'Choisissez une date et une heure qui vous conviennent.')}</p>
                <div className="flex items-center gap-1 mt-3">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}<span className="text-sm font-semibold text-gray-700 ml-1">4.9 Google</span></div>
              </div>
              <Field id="vi-name" label={en ? 'Full name' : 'Nom complet'}><Input id="vi-name" required value={form.name} onChange={set('name')} className={fieldCls} data-testid={`${tab}-name`} /></Field>
              <Field id="vi-email" label="Email"><Input id="vi-email" type="email" required value={form.email} onChange={set('email')} placeholder="jean@exemple.com" className={fieldCls} data-testid={`${tab}-email`} /></Field>
              <Field id="vi-phone" label={en ? 'Phone' : 'Téléphone'}><Input id="vi-phone" type="tel" required value={form.phone} onChange={set('phone')} placeholder="(514) 555-1234" className={fieldCls} data-testid={`${tab}-phone`} /></Field>
              {tab === 'inquiry' ? (
                <Field id="vi-msg" label="Message"><Textarea id="vi-msg" rows={3} value={form.message} onChange={set('message')} className="mt-1.5 bg-gray-50 border-gray-200 rounded-xl resize-none" data-testid="inquiry-message" /></Field>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Field id="vi-date" label="Date"><Input id="vi-date" type="date" min={new Date().toISOString().slice(0, 10)} required value={form.date} onChange={set('date')} className={fieldCls} data-testid="testdrive-date" /></Field>
                  <Field id="vi-time" label={en ? 'Time' : 'Heure'}><Input id="vi-time" type="time" required value={form.time} onChange={set('time')} className={fieldCls} data-testid="testdrive-time" /></Field>
                </div>
              )}
              {status === 'error' && <p className="text-sm text-red-600" data-testid="vehicle-inquiry-error">{en ? 'Something went wrong, please try again.' : 'Une erreur est survenue, veuillez réessayer.'}</p>}
              <button type="submit" disabled={status === 'sending'} className="w-full bg-gradient-to-r from-[#1a5c3b] to-[#165432] text-white font-semibold h-12 rounded-xl shadow-lg shadow-green-700/25 transition-all hover:-translate-y-0.5 disabled:opacity-60" data-testid={`${tab}-submit`}>
                {status === 'sending' ? (en ? 'Sending…' : 'Envoi…') : (en ? 'Send request' : 'Envoyer la demande')}
              </button>
            </form>
          )}
        </div>
        <div className="border-t border-gray-100 p-4 space-y-2 text-sm text-gray-500 text-center">
          <p className="flex items-center justify-center gap-2"><Mail className="w-4 h-4" />info@creditautoreset.ca</p>
          <p className="flex items-center justify-center gap-2"><MapPin className="w-4 h-4" />{en ? 'Serving all of Quebec' : 'Partout au Québec'}</p>
        </div>
      </div>
    </div>
  );
};
